// Slice de UI do CRM. Estado de domínio fica no cache do RTK Query; aqui só
// vive o que é puramente de interface (aba, filtros, seleção bulk, drawer).
import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { selectCurrentUser } from '@/features/auth';
import type { CrmSegment } from './utils/segment';
import type { CarteiraSort } from './api';

export type CrmTab = 'carteira' | 'sem-resposta' | 'reativar' | 'quadros';
export type CarteiraView = 'table' | 'kanban';

export interface CrmUiState {
  tab: CrmTab;
  /** Filtro de loja (só GESTOR). 'all' = todas. */
  storeFilter: string;
  /** Segmento ativo no strip da carteira. 'all' = todos. */
  segment: CrmSegment | 'all';
  search: string;
  sortBy: CarteiraSort;
  view: CarteiraView;
  /** Cliente aberto no drawer. É o id do CONTATO — é por ele que `/crm/clientes/:id` busca. */
  selectedClientId: string | null;
  /**
   * Conversa que originou a abertura do drawer, quando houve uma (o card do
   * Pipeline). É o que dá destino ao "Abrir conversa" do rodapé — pela Carteira,
   * onde a origem é um contato sem conversa em mãos, fica `null` e o botão some.
   */
  selectedClientConversationId: string | null;
  /** Quadro aberto na aba "Quadros". null = nenhum selecionado ainda. */
  selectedBoardId: string | null;
  /** Seleção bulk (follow-up / reativar). Lista de ids. */
  selectedIds: string[];
}

const initialState: CrmUiState = {
  tab: 'carteira',
  storeFilter: 'all',
  segment: 'all',
  search: '',
  sortBy: 'ltv',
  view: 'kanban',
  selectedClientId: null,
  selectedClientConversationId: null,
  selectedBoardId: null,
  selectedIds: [],
};

const crmSlice = createSlice({
  name: 'crmUi',
  initialState,
  reducers: {
    tabChanged(state, action: PayloadAction<{ tab: CrmTab }>) {
      state.tab = action.payload.tab;
      // Trocar de aba zera a seleção bulk — ela não faz sentido cross-aba.
      state.selectedIds = [];
    },
    storeFilterChanged(state, action: PayloadAction<{ storeId: string }>) {
      state.storeFilter = action.payload.storeId;
    },
    segmentChanged(state, action: PayloadAction<{ segment: CrmSegment | 'all' }>) {
      state.segment = action.payload.segment;
    },
    searchChanged(state, action: PayloadAction<{ value: string }>) {
      state.search = action.payload.value;
    },
    sortChanged(state, action: PayloadAction<{ sortBy: CarteiraSort }>) {
      state.sortBy = action.payload.sortBy;
    },
    viewChanged(state, action: PayloadAction<{ view: CarteiraView }>) {
      state.view = action.payload.view;
    },
    clientSelected(
      state,
      action: PayloadAction<{ id: string | null; conversationId?: string | null }>,
    ) {
      state.selectedClientId = action.payload.id;
      state.selectedClientConversationId = action.payload.conversationId ?? null;
    },
    boardSelected(state, action: PayloadAction<{ id: string | null }>) {
      state.selectedBoardId = action.payload.id;
    },
    selectionToggled(state, action: PayloadAction<{ id: string }>) {
      const idx = state.selectedIds.indexOf(action.payload.id);
      if (idx === -1) state.selectedIds.push(action.payload.id);
      else state.selectedIds.splice(idx, 1);
    },
    selectionSet(state, action: PayloadAction<{ ids: string[] }>) {
      state.selectedIds = action.payload.ids;
    },
    selectionCleared(state) {
      state.selectedIds = [];
    },
  },
});

export const {
  tabChanged,
  storeFilterChanged,
  segmentChanged,
  searchChanged,
  sortChanged,
  viewChanged,
  clientSelected,
  boardSelected,
  selectionToggled,
  selectionSet,
  selectionCleared,
} = crmSlice.actions;

export const crmUiReducer = crmSlice.reducer;

type Root = { crmUi: CrmUiState };

export const selectCrmUi = (s: Root): CrmUiState => s.crmUi;
export const selectCrmTab = (s: Root): CrmTab => s.crmUi.tab;
export const selectCrmStoreFilter = (s: Root): string => s.crmUi.storeFilter;
export const selectCrmSegment = (s: Root): CrmSegment | 'all' => s.crmUi.segment;
export const selectCrmSearch = (s: Root): string => s.crmUi.search;
export const selectCrmSortBy = (s: Root): CarteiraSort => s.crmUi.sortBy;
export const selectCrmView = (s: Root): CarteiraView => s.crmUi.view;
export const selectCrmSelectedClientId = (s: Root): string | null => s.crmUi.selectedClientId;
export const selectCrmSelectedClientConversationId = (s: Root): string | null =>
  s.crmUi.selectedClientConversationId;
export const selectCrmSelectedBoardId = (s: Root): string | null => s.crmUi.selectedBoardId;
export const selectCrmSelectedIds = (s: Root): string[] => s.crmUi.selectedIds;

/** `'all'` só vale para GESTOR; para os demais vira "as minhas lojas". */
const ALL_STORES = 'all';

type UserSlice = Parameters<typeof selectCurrentUser>[0];

/**
 * O storeId que vai para a API, já **clampado por papel**.
 *
 * O clamp mora aqui, e não no componente, pelo mesmo motivo do módulo de
 * indicadores (`metrics/slice.ts`): nenhum componente pode esquecer o gate se não
 * houver de onde ler o valor não-clampado.
 *
 * Duas regras:
 * - **`'all'` (rede) é privilégio de GESTOR.** Um gerente/vendedor com `'all'`
 *   no estado cai em `undefined`, e o back devolve as lojas dele — que é o
 *   universo correto, não a rede.
 * - **Loja fora do escopo cai fora.** ⚠️ `storeIds` vazio significa "escopo
 *   desconhecido nesta sessão", não "nenhuma loja": clampar nesse caso esvaziaria
 *   o quadro de quem tem sessão antiga. Quem decide de verdade é o back, que
 *   devolve 404 `STORE_NOT_FOUND`.
 */
export const selectCrmStoreQuery = createSelector(
  [(s: Root & UserSlice) => s.crmUi.storeFilter, (s: Root & UserSlice) => selectCurrentUser(s)],
  (storeFilter, user): string | undefined => {
    const isGestor = user?.role === 'GESTOR';
    if (!storeFilter || storeFilter === ALL_STORES) return undefined;
    if (!isGestor) {
      const scoped = user?.storeIds ?? [];
      if (scoped.length > 0 && !scoped.includes(storeFilter)) return undefined;
    }
    return storeFilter;
  },
);
